
import { useSortable } from '@dnd-kit/sortable';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { blockRegistry } from '@/lib/email-builder/block-registry';
import type { EmailBlock } from '@/types/email-blocks';
import { memo, useMemo } from 'react';

interface BlockRendererProps {
  block: EmailBlock;
  depth?: number; // For visual debugging
  parentId?: string | null; // Parent block ID for drag-and-drop
  index?: number; // Position among siblings for drag-and-drop
}

// Memoized, with narrow store subscriptions: typing in one block or hovering another must not
// re-render every block in the email. Tree updates keep unchanged blocks' object identity.
export const BlockRenderer = memo(function BlockRenderer({
  block,
  depth = 0,
  parentId = null,
  index = 0,
}: BlockRendererProps) {
  const isSelected = useEmailBuilderStore((s) => s.selectedBlockId === block.id);
  const isHovered = useEmailBuilderStore((s) => s.hoveredBlockId === block.id);
  const selectBlock = useEmailBuilderStore((s) => s.selectBlock);
  const hoverBlock = useEmailBuilderStore((s) => s.hoverBlock);

  // Get block configuration from registry
  const blockConfig = blockRegistry.get(block.blockType);

  // Columns are never independently movable — they live inside a section.
  // All other block types (including sections with column children) remain sortable.
  const disabled = block.blockType === 'column';

  // Setup sortable behavior for drag-and-drop
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled: disabled,
    data: {
      type: 'existing-block',
      blockId: block.id,
      parentId: parentId,
      orderIndex: index,
    },
  });

  // Prepare sorted children
  const sortedChildren = useMemo(() => {
    if (!blockConfig?.allowChildren || !block.childBlocks?.length) {
      return [];
    }
    return [...block.childBlocks].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [block.childBlocks, blockConfig]);

  if (!blockConfig) {
    console.warn(`Block type "${block.blockType}" not registered`);
    return (
      <div className="border-2 border-destructive bg-destructive/10 p-4">
        <p className="text-destructive">Unknown block type: {block.blockType}</p>
      </div>
    );
  }

  const BlockComponent = blockConfig.component;

  // Render children recursively if block allows children
  const children = sortedChildren.length > 0 ? (
    <>
      {sortedChildren.map((childBlock, idx) => (
        <BlockRenderer
          key={childBlock.id}
          block={childBlock}
          depth={depth + 1}
          parentId={block.id}
          index={idx}
        />
      ))}
    </>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      data-block-id={block.id}
      data-block-type={block.blockType}
      data-depth={depth}
      className={`
        lw-block-renderer relative transition-all
        ${block.blockType === 'column' ? 'flex-1 min-w-0' : ''}
        ${isDragging ? 'z-50 opacity-40' : ''}
        outline-dashed outline-1
        ${isSelected || isHovered ? 'outline-transparent' : 'outline-muted-foreground/25'}
      `}
    >
      {/* Drag handle - appears on hover or when selected (not for containers with children) */}
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          className={`
            lw-block-drag-handle
            absolute -left-8 top-2 z-10 cursor-grab active:cursor-grabbing
            rounded bg-primary/10 p-1.5 shadow-sm
            opacity-0 transition-opacity hover:opacity-100
            ${isSelected || isHovered ? 'opacity-80' : ''}
          `}
          aria-label="Drag to reorder"
          title="Drag to reorder block"
        >
          <svg
            className="h-4 w-4 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </div>
      )}

      {/* Block content */}
      <div>
        <BlockComponent
          block={block}
          isSelected={isSelected}
          isHovered={isHovered}
          onSelect={selectBlock}
          onHover={hoverBlock}
        >
          {children}
        </BlockComponent>
      </div>
    </div>
  );
});
